'use client';

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

// Component Imports
import Loader from '@/components/loader';
import SearchCategorySorting from '@/components/search/searchCategorySorting';
import ProductCard from '@/components/product/productCard';
import Pagination from '@/components/pagination';
import { defaultStoreNumber } from '@/lib/constants/store';

// Style Imports
import styles from './searchGrid.module.scss';

// Type Imports
import type { NormalizedProduct } from '@/lib/types/products/normalizedProduct';

interface SearchGridProps {
  loading: boolean;
  orderBy?: string;
  searchPage: number;
  searchPageCount: number;
  searchPageSize: number;
  totalProducts: number;
  products: NormalizedProduct[];
  store?: number;
}

const SearchGrid: React.FC<SearchGridProps> = ({
  loading,
  orderBy,
  searchPage,
  searchPageCount,
  searchPageSize,
  totalProducts,
  products,
  store = defaultStoreNumber,
}) => {
  const showingStartNumber = (searchPageSize * searchPage) - searchPageSize + 1;
  const showingEndNumber = showingStartNumber + products.length - 1;

  // Show loader while products are loading
  if (loading && products.length === 0) {
    return <Loader variant="2" />;
  }

  // No results found
  if (!loading && products.length === 0) {
    return (
      <div className={styles.noResults} aria-label="Product search results" role="region"  >
        <FontAwesomeIcon icon={faSearch} size="3x" />
        <p role='alert'>No results found</p>
      </div>
    );
  }

  // Render product results
  return (
    <>
      <div className={styles.sorting}>
        <p role='alert'>
          Showing {showingStartNumber}-{showingEndNumber} of {totalProducts} results
        </p>
        <div className={styles.sortingBy}>
          <SearchCategorySorting
            defaultValues={{
              show: searchPageSize,
              orderby: 'relevance',
            }}
            orderBy={orderBy}
            pageSize={searchPageSize}
            styles={styles}
          />
        </div>
      </div>

      <section className={styles.productList} aria-label="Product search results">
        {products.map((product: NormalizedProduct, index: number) => (
          <ProductCard
            key={`p-${index}`}
            product={product}
            showBorder={true}
          />
        ))}
      </section>

      {searchPageCount > 1 && (
        <Pagination
          styles={styles}
          pageCount={searchPageCount}
          currentPage={searchPage}
        />
      )}
    </>
  );
};

export default SearchGrid;
